import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { listEmployees } from '../api/employees.js';

/**
 * Search-based employee picker for the Loans Portfolio entry points
 * (loan-migration feature). On select, navigates to the per-employee advances
 * page in the chosen mode (?mode=new | ?mode=migration). Lists active employees
 * only — terminated employees don't take new loans.
 */
export function EmployeePickerModal({
  mode,
  onClose,
}: {
  mode: 'new' | 'migration';
  onClose: () => void;
}): JSX.Element {
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['employees', 'picker'],
    queryFn: () => listEmployees({ status: 'active', limit: 200 }),
  });

  const employees = (data?.employees ?? []).filter((e) => {
    if (!q.trim()) return true;
    const hay = `${e.firstName} ${e.lastName} ${e.employeeNumber}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });

  const pick = (id: string): void => {
    onClose();
    void navigate({ to: '/employees/$id/advances', params: { id }, search: { mode } });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={mode === 'migration' ? 'Migrate existing loan — pick employee' : 'Create new loan — pick employee'}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={onClose}
    >
      <div className="bg-gray-950 border border-gray-800 rounded-lg p-5 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-medium text-white mb-3">
          {mode === 'migration' ? 'Migrate existing loan' : 'Create new loan'} — choose employee
        </h2>
        <input
          aria-label="Search employees"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or employee number…"
          className="w-full rounded border border-gray-800 bg-black px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-white"
        />
        <ul className="mt-3 max-h-72 overflow-y-auto divide-y divide-gray-900 border border-gray-900 rounded">
          {isLoading ? (
            <li className="px-3 py-2 text-sm text-gray-500">Loading…</li>
          ) : employees.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-500">No active employees match.</li>
          ) : (
            employees.map((e) => (
              <li key={e.id}>
                <button
                  type="button"
                  onClick={() => pick(e.id)}
                  className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-gray-900"
                >
                  {e.firstName} {e.lastName} <span className="text-gray-500 font-mono text-xs">{e.employeeNumber}</span>
                </button>
              </li>
            ))
          )}
        </ul>
        <div className="mt-4 flex justify-end">
          <button type="button" onClick={onClose} className="rounded border border-gray-700 px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-900">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
